/* FRAMES SHIPPED BY MODEL & COLOUR (task #96 report 2 — Anh Le's "Overseas sales by Model and Color (from 2018~)"; Max
   31/10: from the Order Status master, uploaded whenever she likes; her years stay, the portal counts from Oct 2026).
   ONE TABLE, her Total Frame sheet (Max 31/10: "chỉ cần làm 1 theo sheet total … có filter để chọn tac, outdoor, home và
   collaboration"): model × frame colour by year, the lines picked; Model and Colour filters like her slicers. With all four
   lines the years are hers plus the counted months (marked); with fewer, every year is counted from the Order Status — her
   workbook has frame colours only for the four together — and the screen says so. ONE MONTH: what she sends every month.
   The rules are the server's (modelColor.js); nothing is counted here but the filters.
   Max 01/11: less text on top (the source and the rules fold under one line), every filter a dropdown with search and
   several choices (Line, Model, Frame colour, Year — her slicers), the Total row right under the header. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Upload, X, Info, LayoutDashboard, Table2, CalendarDays } from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import useAuth from './useAuth';
import { MultiPick } from './57_InventoryReport';
import ModelColorDashboard from './ModelColorDashboard';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './InventoryReport.css';
import './SalesByBuyer.css';
import './ModelColor.css';

const API = `${BACKEND_URL}/api/model-color`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const n0 = (v) => (v ? Math.round(v).toLocaleString('en-US') : '');
const dmy = (d) => (d ? `${d.slice(8, 10)}/${MON[Number(d.slice(5, 7)) - 1]}/${d.slice(0, 4)}` : '');
const LINES = [['outdoor', 'Outdoor'], ['tactical', 'Tactical'], ['home', 'Home'], ['collab', 'Collaboration']];
const lastMonth = () => { const n = new Date(Date.now() + 7 * 3600000); const d = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth() - 1, 1)); return d.toISOString().slice(0, 7); };

function LinesDialog({ ask, onClose, t }) {
  const [lines, setLines] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const qs = new URLSearchParams({ lines: ask.lines, model: ask.model, color: ask.color, ...(ask.ym ? { ym: ask.ym } : { yr: String(ask.yr) }) });
    fetch(`${API}/lines?${qs}`, { credentials: 'include' })
      .then((r) => r.json().then((d) => (r.ok ? setLines(d.lines) : setErr(d.error || 'Error')))).catch((e) => setErr(e.message));
  }, [ask]);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="sbc-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sbc-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-lines-t">
        <div className="sbc-dialog-head">
          <h3 id="mc-lines-t">{ask.model} · {ask.color} · {ask.ym ? `${MON[Number(ask.ym.slice(5)) - 1]}/${ask.ym.slice(0, 4)}` : ask.yr}</h3>
          <button type="button" className="sbc-x" onClick={onClose} aria-label={t('xModelColor.close')}><X size={16} /></button>
        </div>
        {!ask.ym && ask.workbook && <p className="sbc-muted">{t('xModelColor.linesYearNote')}</p>}
        {err && <p className="sbc-err" role="alert">{err}</p>}
        {!lines && !err && <p>{t('xModelColor.loading')}</p>}
        {lines && (
          <table className="pa-table sbc-docs">
            <thead><tr><th>S/D</th><th>PO No.</th><th>{t('xModelColor.buyer')}</th><th>Corp.</th><th>SAP Item</th><th>Item</th><th className="num">Q&apos;ty</th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.row_no}><td>{l.sd}</td><td>{l.po_no}</td><td>{l.buyer}</td><td>{l.overseas_corp}</td><td>{l.sap_item}</td><td>{l.item}</td><td className="num">{n0(l.qty)}</td></tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={6}>{t('xModelColor.total')} · {t('xModelColor.nLines', { n: lines.length })}</td><td className="num">{n0(lines.reduce((s, l) => s + l.qty, 0))}</td></tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). The views are state inside the screen, so a step that lives on another view
   clicks that view's own tab first (`prepare`); the tour opens on the Dashboard (onBeforeTour). The upload button is drawn
   only for people who may upload, so it is described in the Excel step rather than given one of its own. */
const clickView = (k) => () => document.querySelector(`[data-mc-view-${k}]`)?.click();
const TOUR = [
  { selector: '.mc-page .view-tabs', title: 'tourModelColor.tabsT', body: 'tourModelColor.tabsB' },
  { selector: '.mc-filters', title: 'tourModelColor.filtersT', body: 'tourModelColor.filtersB', prepare: clickView('dash') },
  { selector: '.mc-page .view-tabs-tools', title: 'tourModelColor.toolsT', body: 'tourModelColor.toolsB' },
  { selector: '[data-mcd-kpis]', title: 'tourModelColor.dashT', body: 'tourModelColor.dashB', prepare: clickView('dash') },
  { selector: '.mc-table', title: 'tourModelColor.yearT', body: 'tourModelColor.yearB', prepare: clickView('year') },
  { selector: '.mc-monthfield', title: 'tourModelColor.monthT', body: 'tourModelColor.monthB', prepare: clickView('month') },
];

export default function ModelColor() {
  const { t } = useTranslation();
  const { permissions = {} } = useAuth();
  const canEdit = permissions.sales_model_color === 'edit';
  const [status, setStatus] = useState(null);
  const [view, setView] = useState('dash');
  const [lines, setLines] = useState([]);        // labels picked; none = all four
  const [fModels, setFModels] = useState([]);
  const [fColors, setFColors] = useState([]);
  const [fYears, setFYears] = useState([]);
  const [year, setYear] = useState(null);
  const [ym, setYm] = useState(lastMonth());
  const [month, setMonth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [ask, setAsk] = useState(null);
  const fileRef = useRef(null);
  const lineKeys = LINES.filter(([, l]) => lines.includes(l)).map(([k]) => k);
  const lineQs = lineKeys.join(',');            // '' = all four (the server's default)

  const loadStatus = useCallback(() => fetch(`${API}/status`, { credentials: 'include' }).then((r) => r.json()).then(setStatus).catch(() => {}), []);
  /* only the newest request may answer: a filter changed while an older one is still on its way must not be overwritten */
  const seq = useRef(0);
  const loadYear = useCallback(async () => {
    setErr('');
    const mine = ++seq.current;
    try {
      const r = await fetch(`${API}/frame?lines=${lineQs}`, { credentials: 'include' });
      const d = await r.json();
      if (mine !== seq.current) return;
      if (!r.ok) throw new Error(d.error || 'Error');
      setYear(d);
    } catch (e) { setErr(e.message); }
  }, [lineQs]);
  const loadMonth = useCallback(async () => {
    setErr('');
    const mine = ++seq.current;
    try {
      const r = await fetch(`${API}/month?ym=${ym}&lines=${lineQs}`, { credentials: 'include' });
      const d = await r.json();
      if (mine !== seq.current) return;
      if (!r.ok) throw new Error(d.error || 'Error');
      setMonth(d);
    } catch (e) { setErr(e.message); }
  }, [ym, lineQs]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  /* the dashboard draws the by-year rows, so it reads the same data */
  const yearly = view === 'year' || view === 'dash';
  useEffect(() => { if (yearly) { setYear(null); loadYear(); } }, [yearly, loadYear]);
  useEffect(() => { if (view === 'month') { setMonth(null); loadMonth(); } }, [view, loadMonth]);

  const onPick = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API}/upload?name=${encodeURIComponent(f.name)}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: f });
      const d = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
      if (!r.ok) throw new Error(d.error || 'Error');
      setMsg(t('xModelColor.uploaded', { n: d.upload.row_count.toLocaleString('en-US'), from: dmy(d.upload.sd_min), to: dmy(d.upload.sd_max) }));
      await loadStatus();
      if (view === 'year') await loadYear(); else await loadMonth();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  const up = status?.upload;
  const countFromYear = status ? Number(status.countFrom.slice(0, 4)) : 2026;
  const fromWorkbook = year?.source === 'workbook';
  const source = useMemo(() => (view !== 'month' ? year?.rows || [] : month?.rows || []), [view, year, month]);
  const modelOpts = useMemo(() => [...new Set(source.map((r) => r.model))].sort().map((v) => ({ v })), [source]);
  const colorOpts = useMemo(() => [...new Set(source.filter((r) => !fModels.length || fModels.includes(r.model)).map((r) => r.color))].sort().map((v) => ({ v })), [source, fModels]);
  const yearOpts = useMemo(() => (year?.years || []).map((y) => ({ v: String(y) })), [year]);
  const keep = useCallback((r) => (!fModels.length || fModels.includes(r.model)) && (!fColors.length || fColors.includes(r.color)), [fModels, fColors]);
  /* the year columns shown — her Year slicer; the Total is over these */
  const cols = useMemo(() => (year ? year.years.map((y, i) => ({ y, i })).filter(({ y }) => !fYears.length || fYears.includes(String(y))) : []), [year, fYears]);
  const shown = useMemo(() => (year?.rows || []).filter(keep)
    .map((r) => ({ ...r, sub: cols.reduce((s, { i }) => s + (r.years[i] || 0), 0) })).filter((r) => r.sub), [year, keep, cols]);
  const colTotals = useMemo(() => cols.map(({ i }) => shown.reduce((s, r) => s + (r.years[i] || 0), 0)), [cols, shown]);
  const monthRows = useMemo(() => (month?.rows || []).filter(keep), [month, keep]);
  const badYears = (year?.check || []).filter((c) => Math.round(c.lines) !== Math.round(c.frame) && (c.lines || c.frame));
  const pick = (label, opts, value, onChange, id) => <MultiPick t={t} label={label} options={opts} value={value} onChange={onChange} testId={id} />;

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main sbc-page mc-page">
        {/* the title and the tab bar stay put while the page scrolls (Max 01/11: "freeze khúc trên lại") */}
        <div className="page-sticky">
        <div className="sbc-head">
          <div>
            <h2 className="pa-title">{t('xModelColor.title')}</h2>
            {/* one line on top (Max 01/11: "bị nhiều chữ") — what the numbers are made of folds under it */}
            <details className="mc-about" data-mc-about>
              <summary>
                <Info size={13} aria-hidden="true" />
                {up ? t('xModelColor.sourceShort', { at: dmy(up.at.slice(0, 10)), to: dmy(up.sd_max) }) : t('xModelColor.noSource')}
                {year?.check && !badYears.length && <span className="mc-chip ok">✓ {t('xModelColor.checkOk')}</span>}
                {badYears.length > 0 && <span className="mc-chip bad" data-mc-check>⚠ {badYears.map((c) => `${c.year}: ${n0(c.lines)} ≠ ${n0(c.frame)}`).join(' · ')}</span>}
              </summary>
              <p>{t('xModelColor.sub')}</p>
              {up && <p>{t('xModelColor.source', { file: up.file_name, at: up.at, by: up.uploaded_by, n: Number(up.row_count).toLocaleString('en-US'), to: dmy(up.sd_max) })}</p>}
              <p>{fromWorkbook || view === 'month' ? t('xModelColor.legend', { from: status ? dmy(status.countFrom) : '' }) : t('xModelColor.someLinesNote')}</p>
              {year?.check && <p>{t('xModelColor.checkTitle')} {year.check.filter((c) => c.frame || c.lines).map((c) => `${c.year}: ${n0(c.lines)} ${Math.round(c.lines) === Math.round(c.frame) ? '=' : '≠'} ${n0(c.frame)}`).join(' · ')}</p>}
            </details>
          </div>
          <ModuleHelp steps={TOUR} tourKey="framesByModel" ready={!!status && !!year} onBeforeTour={() => setView('dash')} />
        </div>
        {busy && <p className="sbc-muted" role="status">{t('xModelColor.readingLong')}</p>}
        {msg && <p className="sbc-ok" role="status">{msg}</p>}
        {err && <p className="sbc-err" role="alert">{err}</p>}

        {/* the views as a tab bar of their own under the title (Max 01/11: "làm sao cho tab này nổi bật hơn") */}
        {/* Excel + Upload at the right end of the tab bar (Max 02/11: "dời nút xuống đây") */}
        <div className="view-tabs-row">
          <div className="view-tabs" role="tablist" aria-label={t('xModelColor.title')}>
            <button type="button" role="tab" aria-selected={view === 'dash'} className={view === 'dash' ? 'on' : ''} onClick={() => setView('dash')} data-mc-view-dash><LayoutDashboard size={16} aria-hidden="true" /> {t('xModelColor.dashboard')}</button>
            <button type="button" role="tab" aria-selected={view === 'year'} className={view === 'year' ? 'on' : ''} onClick={() => setView('year')} data-mc-view-year><Table2 size={16} aria-hidden="true" /> {t('xModelColor.byYear')}</button>
            <button type="button" role="tab" aria-selected={view === 'month'} className={view === 'month' ? 'on' : ''} onClick={() => setView('month')} data-mc-view-month><CalendarDays size={16} aria-hidden="true" /> {t('xModelColor.oneMonth')}</button>
          </div>
          <div className="view-tabs-tools">
            <a className="pa-btn" href={`${API}/xlsx?lines=${lineQs}`} download><FileSpreadsheet size={14} /> {t('xModelColor.excel')}</a>
            {canEdit && (
              <>
                <button type="button" className="pa-btn primary" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} data-mc-upload>
                  <Upload size={14} /> {busy ? t('xModelColor.reading') : t('xModelColor.upload')}
                </button>
                <input ref={fileRef} type="file" accept=".xlsx,.xlsm" hidden onChange={onPick} />
              </>
            )}
          </div>
        </div>
        </div>
        <div className="mc-filters">
          {pick(t('xModelColor.lines'), LINES.map(([, l]) => ({ v: l })), lines, setLines, 'line')}
          {pick('Model', modelOpts, fModels, (v) => { setFModels(v); setFColors((c) => c.filter((x) => source.some((r) => (!v.length || v.includes(r.model)) && r.color === x))); }, 'model')}
          {pick(t('xModelColor.frameColor'), colorOpts, fColors, setFColors, 'color')}
          {view !== 'month' && pick(t('xModelColor.year'), yearOpts, fYears, setFYears, 'year')}
          {/* the month sits with the other filters (Max 01/11: "cho month lên trên") */}
          {view === 'month' && (
            <label className="iv-field mc-monthfield"><span>{t('xModelColor.month')}</span>
              <input type="month" value={ym} onChange={(e) => e.target.value && setYm(e.target.value)} data-mc-month />
            </label>
          )}
        </div>
        {view !== 'month' && year && !fromWorkbook && <p className="mc-note" data-mc-os-note>{t('xModelColor.someLinesNote')}</p>}
        {view === 'dash' && (year ? <ModelColorDashboard rows={shown} cols={cols} /> : !err && <p>{t('xModelColor.loading')}</p>)}

        {view === 'year' && (
          <>
            {!year && !err && <p>{t('xModelColor.loading')}</p>}
            {year && (
              <div className="sbc-wrap">
                <table className="sbc-table mc-table" style={{ width: 200 + 190 + cols.length * 86 + 96 }}>
                  <colgroup><col style={{ width: 200 }} /><col style={{ width: 190 }} />{cols.map(({ y }) => <col key={y} style={{ width: 86 }} />)}<col style={{ width: 96 }} /></colgroup>
                  <thead>
                    <tr>
                      <th className="mc-c1">Model</th><th className="mc-c2">{t('xModelColor.frameColor')}</th>
                      {cols.map(({ y }) => <th key={y} className="num">{y}{(!fromWorkbook || y >= countFromYear) && <span className="sbc-src" title={t('xModelColor.countedHint')}>+OS</span>}</th>)}
                      <th className="num">{t('xModelColor.total')}</th>
                    </tr>
                    {/* the Total right under the header (Max 01/11), held there while the rows scroll */}
                    <tr className="mc-totrow" data-mc-totrow>
                      <th className="mc-c1" colSpan={2}>{t('xModelColor.total')}</th>
                      {colTotals.map((v, j) => <th key={cols[j].y} className="num">{n0(v)}</th>)}
                      <th className="num">{n0(colTotals.reduce((a, b) => a + b, 0))}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r, i) => (
                      <tr key={`${r.model}|${r.color}`}>
                        <td className="mc-c1">{i > 0 && shown[i - 1].model === r.model ? '' : r.model}</td>
                        <td className="mc-c2">{r.color}</td>
                        {cols.map(({ y, i: j }) => (
                          <td key={y} className={`num${r.counted[j] && fromWorkbook ? ' mc-counted' : ''}`}>
                            {r.counted[j]
                              ? <button type="button" className="sbc-cellbtn" title={fromWorkbook ? t('xModelColor.countedCell', { n: n0(r.counted[j]) }) : t('xModelColor.seeLines')}
                                onClick={() => setAsk({ lines: lineQs, workbook: fromWorkbook, model: r.model, color: r.color, yr: y })}>{n0(r.years[j])}</button>
                              : n0(r.years[j])}
                          </td>
                        ))}
                        <td className="num sbc-tot">{n0(r.sub)}</td>
                      </tr>
                    ))}
                    {!shown.length && <tr><td colSpan={cols.length + 3} className="sbc-muted">{t('xModelColor.none')}</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {view === 'month' && (
          <>
            {month?.before && <p className="mc-note">{t('xModelColor.beforeNote')}</p>}
            {month?.soFar && <p className="mc-note">{t('xModelColor.soFarNote', { d: dmy(month.soFar) })}</p>}
            {!month && !err && <p>{t('xModelColor.loading')}</p>}
            {month && !month.upload && <p className="sbc-muted">{t('xModelColor.noSource')}</p>}
            {month && month.upload && (
              <section className="mc-card mc-monthcard" aria-labelledby="mc-m-t">
                <h3 id="mc-m-t">{(lines.length ? lines : LINES.map(([, l]) => l)).join(' · ')} <span data-mc-month-total>{n0(monthRows.reduce((s, r) => s + r.qty, 0)) || '0'}</span></h3>
                {monthRows.length ? (
                  /* its own scroll box, so the header row stays put while the rows move (Max 01/11) */
                  <div className="mc-monthwrap">
                  <table className="pa-table mc-monthtable" data-mc-monthtable>
                    <thead><tr><th>Model</th><th>{t('xModelColor.frameColor')}</th><th className="num">Q&apos;ty</th></tr></thead>
                    <tbody>
                      {monthRows.map((r) => (
                        <tr key={`${r.model}|${r.color}`}><td>{r.model}</td><td>{r.color}</td>
                          <td className="num"><button type="button" className="sbc-cellbtn" onClick={() => setAsk({ lines: lineQs, model: r.model, color: r.color, ym })}>{n0(r.qty)}</button></td></tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : <p className="sbc-muted">{t('xModelColor.none')}</p>}
              </section>
            )}
          </>
        )}
        {ask && <LinesDialog ask={ask} onClose={() => setAsk(null)} t={t} />}
      </div>
    </div>
  );
}
